import {
  FunctionComponent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Credit,
  Introduction,
  Job,
  JobCompanyInfo,
  actions,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
// import { useToast } from '@kanda-libs/ks-design-library';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';
import { reloadInitialData } from 'store/slices/app';

export const SatNotePages = ['initial', 'terms', 'questions'] as const;

export type SatNotePage = (typeof SatNotePages)[number];

export interface SatNoteContextProps {
  children: ReactNode;
}

export interface SatNoteContextType {
  isIntroduced: boolean;
  isSupplyOnly: boolean;
  job?: Job;
  credit?: Credit;
  companyInfo?: JobCompanyInfo;
  introduction?: Introduction;
  page: SatNotePage;
  setPage: React.Dispatch<React.SetStateAction<SatNotePage>>;
  toNextPage: () => void;
  toPrevPage: () => void;
  isLoading: boolean;
}

export const SatNoteContext = createContext<SatNoteContextType>({
  isIntroduced: false,
  isSupplyOnly: false,
  job: undefined,
  credit: undefined,
  companyInfo: undefined,
  page: 'initial',
  setPage: () => {},
  toNextPage: () => {},
  toPrevPage: () => {},
  isLoading: false,
});

export const useSatNoteContext = () => useContext(SatNoteContext);

const SatNoteProvider: FunctionComponent<SatNoteContextProps> = function ({
  children,
}) {
  const {
    isLoading: statusIsLoading,
    job,
    credit,
    introduction,
    jobCompanyInfo,
  } = useStatusContext();

  const [page, setPage] = useState<SatNotePage>('initial');

  const viewed = useMemo(() => {
    if (!job) return false;
    return job?.sat_note_timeline?.viewed_at;
  }, [job]);

  const dispatch = useAppDispatch();
  // const { showError } = useToast();
  const handleViewedError = useApiError('Unknown error viewing sat note');

  const toNextPage = useCallback(() => {
    const index = SatNotePages.indexOf(page);
    if (index === SatNotePages.length) return;
    const nextPage = SatNotePages[index + 1];
    setPage(nextPage);
  }, [page]);

  const toPrevPage = useCallback(() => {
    const index = SatNotePages.indexOf(page);
    if (index === 0) return;
    const prevPage = SatNotePages[index - 1];
    setPage(prevPage);
  }, [page]);

  const isLoading = statusIsLoading || !viewed;

  const isIntroduced = useMemo(() => {
    if (!job) return false;
    return Boolean(job?.introduced_to);
  }, [job]);

  const isSupplyOnly = useMemo(() => {
    if (!job) return false;
    return Boolean(job?.work_type?.includes('supply_only'));
  }, [job]);

  const submitViewedRef = useRef<boolean>(false);
  useEffect(() => {
    if (statusIsLoading || !job || viewed || submitViewedRef.current) return;
    const id = job?.id;
    if (!id) return;
    const hasViewed = job?.sat_note_timeline?.viewed_at;
    if (hasViewed) return;
    submitViewedRef.current = true;
    dispatch(
      actions.viewJobSatNote({
        params: {
          id,
        },
        onError: handleViewedError,
        onSuccess: () => {
          submitViewedRef.current = true;
          dispatch(reloadInitialData());
        },
      }),
    );
  }, [statusIsLoading, job, viewed, handleViewedError, dispatch]);

  const values = useMemo(
    () => ({
      isIntroduced,
      isSupplyOnly,
      job,
      credit,
      companyInfo: jobCompanyInfo,
      introduction,
      page,
      setPage,
      toNextPage,
      toPrevPage,
      isLoading,
    }),
    [
      isIntroduced,
      isSupplyOnly,
      job,
      credit,
      jobCompanyInfo,
      introduction,
      page,
      setPage,
      toNextPage,
      toPrevPage,
      isLoading,
    ],
  );

  return (
    <SatNoteContext.Provider value={values}>{children}</SatNoteContext.Provider>
  );
};

export default SatNoteProvider;
